import React, { useCallback, useMemo } from "react";
import { GenericMultiSelectModal } from "../GenericMultiSelectModal";
import { useTranslation } from "react-i18next";
import { PricingDetails } from "./PricingDetails";
import { ComponentItem } from "components/generic-multiselect/types";
import {
    CreatorProduct,
    CreatorProductEntitlementsBindingModelDiscriminator
} from "@switcherstudio/switcher-api-client";

export interface PricingSelectModalProps {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    /** callback function to be called on modal's onSubmit. returns list of selected id (singular) */
    handleSelect: (selection: CreatorProduct) => void;
    /** Filters available options by the type of entitlement that is applicable */
    discriminator?: CreatorProductEntitlementsBindingModelDiscriminator;
    products: CreatorProduct[];
    preventDismiss?: boolean;
    selected?: string;
    buttonText?: string;
}

export const PricingSelectModal = ({
    isOpen,
    setIsOpen,
    handleSelect,
    products,
    discriminator,
    preventDismiss = false,
    selected,
    buttonText
}: PricingSelectModalProps) => {
    const { t } = useTranslation();

    const handlePricingSelect = useCallback(
        (ids: string[]) => {
            handleSelect(
                products.find((product) => {
                    return ids.some((id) => id === product.Id);
                })
            );
        },
        [handleSelect, products]
    );

    const byDiscriminator = useCallback(
        (product: CreatorProduct) => {
            switch (discriminator) {
                case CreatorProductEntitlementsBindingModelDiscriminator._0:
                    return product.Prices.some((p) => p.IsRecurring);
                case CreatorProductEntitlementsBindingModelDiscriminator._2:
                    return product.Prices.some((p) => !p.IsRecurring);
                default:
                    return product.Prices;
            }
        },
        [discriminator]
    );

    const filteredProducts = useMemo(
        () =>
            products?.filter(byDiscriminator)?.filter(
                (p) =>
                    p.CreatorProductEntitlements.length === 0 ||
                    // @ts-expect-error - Handle multiple types
                    p.CreatorProductEntitlements.some((cpe) => !cpe?.CatalogId)
            ),
        [products, byDiscriminator]
    );

    const previouslySelectedIdsArray = useMemo(
        () => (selected ? [selected] : []),
        [selected]
    );

    return (
        <>
            {filteredProducts && (
                <GenericMultiSelectModal
                    isMultiple={false}
                    onSubmit={handlePricingSelect}
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    submitHandlesClose={true}
                    previouslySelectedIds={previouslySelectedIdsArray}
                    allowUnselect={true}
                    titleText={t("gated-content-modal:choose-pricing")}
                    buttonText={buttonText ?? t("buttons:save")}
                    allowNoSelection={true}
                    preventDismiss={preventDismiss}
                    showNone={true}
                    items={filteredProducts.map((p) => {
                        return {
                            id: p.Id,
                            component: <PricingDetails item={p} />
                        } as ComponentItem<CreatorProduct>;
                    })}
                />
            )}
        </>
    );
};
